import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { ArrowRight, Award, HeadsetIcon, Cpu, ShieldCheck, Youtube, ExternalLink } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import injectionImg from "@/assets/injection-machine.webp";
import robotImg from "@/assets/robot.webp";
import auxiliariesImg from "@/assets/auxiliaries.webp";
import centralImg from "@/assets/central-systems.webp";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function Index() {
  const { t } = useLanguage();

  const equipmentCards = [
    { img: injectionImg, label: t("nav.injectionMachines"), desc: t("equipment.injectionDesc"), path: "/echipamente/masini-de-injectie" },
    { img: robotImg, label: t("nav.robots"), desc: t("equipment.robotsDesc"), path: "/echipamente/roboti" },
    { img: auxiliariesImg, label: t("nav.auxiliaries"), desc: t("equipment.auxiliariesDesc"), path: "/echipamente/auxiliare" },
    { img: centralImg, label: t("nav.centralSystems"), desc: t("equipment.centralDesc"), path: "/echipamente/sisteme-centralizate" },
  ];

  const whyCards = [
    { icon: Award, title: t("home.whyExperience"), desc: t("home.whyExperienceDesc") },
    { icon: ShieldCheck, title: t("home.whyQuality"), desc: t("home.whyQualityDesc") },
    { icon: HeadsetIcon, title: t("home.whySupport"), desc: t("home.whySupportDesc") },
    { icon: Cpu, title: t("home.whyIntegration"), desc: t("home.whyIntegrationDesc") },
  ];

  return (
    <>
      <SEOHead
        title="WITTMANN ROMANIA | Mașini de injecție, roboți industriali, echipamente mase plastice"
        description="WITTMANN ROMANIA - Mașini de injecție, roboți industriali, termoregulatoare, uscător plastic și sisteme centralizate. Soluții complete pentru injecție plastic și prelucrare mase plastice."
        path="/"
        keywords="masini de injectie, injectie plastic, termoregulatoare, roboti industriali, robot plastice, mase plastice, uscator plastic, WITTMANN Romania, echipamente plastic"
      />
      {/* Hero */}
      <section className="relative h-[85vh] min-h-[500px] flex items-center">
        <img src="/hero-bg.webp" alt="WITTMANN factory" className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" decoding="async" />
        <div className="hero-overlay absolute inset-0" />
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-background mb-4 leading-tight">
              {t("home.heroTitle")}
            </h1>
            <p className="text-lg sm:text-xl text-background/80 mb-2 leading-relaxed">
              {t("home.heroSubtitle")}
            </p>
            <p className="text-sm sm:text-base text-background/60 mb-8 tracking-wide">
              {t("home.heroProducts")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/echipamente"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                {t("home.heroCta")}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/promotii"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                {t("nav.promotions")}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-background/30 text-background font-semibold rounded-lg hover:bg-background/10 transition-colors text-sm"
              >
                {t("home.heroContact")}
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <a
                href="https://www.youtube.com/@WittmannRomania"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[hsl(0,100%,50%)] text-background font-semibold rounded-lg hover:bg-[hsl(0,100%,40%)] transition-colors text-sm"
              >
                <Youtube className="w-4 h-4" />
                YouTube
              </a>
              <a
                href="https://www.wittmann-group.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-background/30 text-background font-semibold rounded-lg hover:bg-background/10 transition-colors text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                WITTMANN Global
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="section-container">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
              {t("home.sectionEquipment")}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t("home.sectionEquipmentDesc")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {equipmentCards.map((card, i) => (
              <motion.div
                key={card.path}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Link
                  to={card.path}
                  className="group block relative h-80 rounded-xl overflow-hidden"
                >
                  <img
                    src={card.img}
                    alt={card.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="equipment-card-overlay absolute inset-0" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-bold text-background text-lg mb-1">{card.label}</h3>
                    <p className="text-background/70 text-sm line-clamp-2">{card.desc}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why WITTMANN */}
      <section className="py-20 lg:py-28 bg-secondary">
        <div className="section-container">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
              {t("home.whyUs")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyCards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-card rounded-xl p-6 border border-border"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <card.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{card.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
